import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  ClockIcon,
  TicketIcon,
  ReceiptPercentIcon,
  GlobeAltIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import {
  ArrowTopRightOnSquareIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import Dropdown from "../forms/Dropdown";
import HorizontalRadio from "../forms/HorizontalRadio";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import UploadFileDropzone from "../forms/UploadImageDropzone";
import Card from "../layout/Card";
import {
  WeblinkState,
  createWeblink,
  deleteWeblinks,
  getWeblinks,
  updateWeblink,
} from "../../slices/content";
import SwitchToggle from "../forms/SwitchToggle";
import DangerZone from "../forms/DangerZone";
import Indicator from "../layout/Indicator";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Weblinks() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getWeblinks());
    dispatch(getRooms());
  }, [dispatch]);

  const [selectedWeblink, setSelectedWeblink] = useState<{
    id: string;
    name: { [key: string]: string };
    description: { [key: string]: string };
    type: string;
    target: string;
    rooms: string[];
    image: string | null;
    url: string;
    qr_code: string | null;
    status: string;
  }>({
    id: "",
    name: { EN: "", FR: "", IT: "", DE: "" },
    description: { EN: "", FR: "", IT: "", DE: "" },
    type: "W",
    target: "A",
    rooms: [],
    image: null,
    url: "",
    qr_code: null,
    status: "A",
  });
  const [createWeblinkForm, setCreateWeblinkForm] = useState<{
    name: { [key: string]: string };
    description: { [key: string]: string };
    type: string;
    target: string;
    rooms: string[];
    url: string;
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    description: { EN: "", FR: "", IT: "", DE: "" },
    type: "W",
    target: "A",
    rooms: [],
    url: "",
  });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showQrCode, setShowQrCode] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { weblinks, options } = useAppSelector((state) => state.content);
  const { rooms } = useAppSelector((state) => state.rooms);
  const { images } = useAppSelector((state) => state.uploads);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectWeblinkToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [weblinkId] = args;
    const weblinkData = weblinksData.filter(
      (weblink) => weblink.id === weblinkId,
    )[0];
    setSelectedWeblink({
      id: weblinkData.id,
      name: weblinkData.name,
      description: weblinkData.description,
      type: weblinkData.type[0],
      target: "S",
      rooms: weblinkData.rooms,
      image: weblinkData.image,
      url: weblinkData.url,
      qr_code: weblinkData.qr_code,
      status: weblinkData.status[0],
    });
    setShowEditModal(true);
  }

  function openCreateWeblinkModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllCreateRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreateWeblinkForm({ ...createWeblinkForm, rooms: allAvailableRooms });
    } else {
      setCreateWeblinkForm({ ...createWeblinkForm, rooms: [] });
    }
  }

  function updateRoomForCreateWeblink(roomId: string) {
    if (createWeblinkForm.rooms.includes(roomId)) {
      const updatedRooms = createWeblinkForm.rooms.filter((x) => x !== roomId);
      setCreateWeblinkForm({ ...createWeblinkForm, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createWeblinkForm.rooms, roomId];
      setCreateWeblinkForm({ ...createWeblinkForm, rooms: updatedRooms });
    }
  }

  function selectOrUnselectAllEditRooms(e: any, args: any[]) {
    e.preventDefault();
    if (selectedWeblink === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setSelectedWeblink({ ...selectedWeblink, rooms: allAvailableRooms });
    } else {
      setSelectedWeblink({ ...selectedWeblink, rooms: [] });
    }
  }

  function updateRoomForEditWeblink(roomId: string) {
    if (selectedWeblink === null) return;
    if (selectedWeblink.rooms.includes(roomId)) {
      const updatedRooms = selectedWeblink.rooms.filter((x) => x !== roomId);
      setSelectedWeblink({ ...selectedWeblink, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createWeblinkForm.rooms, roomId];
      setSelectedWeblink({ ...selectedWeblink, rooms: updatedRooms });
    }
  }

  async function confirmCreateWeblink(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    if (rooms.data === null) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      description,
      type,
      url,
      target,
      rooms: chosenRooms,
    } = createWeblinkForm;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (url === "") {
      dispatch(
        addAlert(generateAlertText("missing_url", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let selectedRooms = chosenRooms;
    if (target === "A") {
      selectedRooms = rooms.data.map((x) => x.id);
    }
    if (selectedRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }

    // Add item image if uploaded
    const processedPhotoKeys = Object.keys(images.weblinks.data);
    const processedPhotos = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.weblinks.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }

    setShowCreateModal(false);
    const res: any = await dispatch(
      createWeblink({
        name,
        description,
        type,
        property: user.property.id,
        image: processedPhotos.length > 0 ? processedPhotos[0] : null,
        url,
        rooms: selectedRooms,
        status: "A",
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(
          generateAlertText("create_weblink", language),
          "success",
          3000,
        ),
      );
      setCreateWeblinkForm({
        name: { EN: "", FR: "", IT: "", DE: "" },
        description: { EN: "", FR: "", IT: "", DE: "" },
        type: "W",
        url: "",
        target: "A",
        rooms: [],
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditWeblink(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    if (rooms.data === null) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      description,
      type,
      image,
      url,
      target,
      rooms: chosenRooms,
      status,
    } = selectedWeblink;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (url === "") {
      dispatch(
        addAlert(generateAlertText("missing_url", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let selectedRooms = chosenRooms;
    if (target === "A") {
      selectedRooms = rooms.data.map((x) => x.id);
    }
    if (selectedRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }

    // Confirm and validate image
    const savedImage: any = image;

    // Build object for processed photos
    const processedPhotoKeys = Object.keys(images.weblinks.data);
    const processedPhotos: any = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.weblinks.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }
    let editImage: any = null;
    if (!images.weblinks.removed.includes(savedImage)) {
      editImage = { id: 0, source: "saved", data: savedImage };
    } else {
      editImage = processedPhotos.length > 0 ? processedPhotos[0] : null;
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updateWeblink({
        weblinkId: id,
        name,
        description,
        type,
        property: user.property.id,
        rooms: selectedRooms,
        image: editImage,
        url,
        status,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_weblink", language),
          "success",
          3000,
        ),
      );
      setSelectedWeblink({
        id: "",
        name: { EN: "", FR: "", IT: "", DE: "" },
        description: { EN: "", FR: "", IT: "", DE: "" },
        type: "W",
        target: "A",
        rooms: [],
        image: null,
        url: "",
        qr_code: null,
        status: "A",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteWeblink(e: any, args: any[]) {
    e.preventDefault();
    const [weblinkIds] = args;
    if (weblinkIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (weblinkIds === null || weblinkIds === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_weblinks", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(
      deleteWeblinks({ weblinkIds: [weblinkIds] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function getSavedImages() {
    if (selectedWeblink === null) return [];
    if (selectedWeblink.image === null) return [];
    if (images.weblinks.removed.includes(selectedWeblink.image)) return [];
    return [{ id: selectedWeblink.image, data: selectedWeblink.image }];
  }

  function getFeatureIcon(weblink: any) {
    if (weblink.type[0] === "W") {
      return (
        <div className="inline-flex text-sm text-secondary">
          <GlobeAltIcon className="h-5 w-5 text-info mr-1" aria-hidden="true" />
          {weblink.type[1]}
        </div>
      );
    } else if (weblink.type[0] === "T") {
      return (
        <div className="inline-flex text-sm text-secondary">
          <TicketIcon className="h-5 w-5 text-info mr-1" aria-hidden="true" />
          {weblink.type[1]}
        </div>
      );
    } else if (weblink.type[0] === "V") {
      return (
        <div className="inline-flex text-sm text-secondary">
          <ReceiptPercentIcon
            className="h-5 w-5 text-info mr-1"
            aria-hidden="true"
          />
          {weblink.type[1]}
        </div>
      );
    } else {
      return (
        <div className="inline-flex text-sm text-secondary">
          <GlobeAltIcon className="h-5 w-5 text-info mr-1" aria-hidden="true" />
          {weblink.type[1]}
        </div>
      );
    }
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const weblinksData = weblinks.data === null ? [] : weblinks.data;
  const targetTypes = [
    ["A", translateText("all", language)],
    ["S", translateText("specific", language)],
  ];
  const weblinkTypeChoices =
    options.type === undefined
      ? []
      : Object.keys(options.type).map((key) => [
          key,
          options.type[key][language],
        ]);

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("weblinks_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateWeblinkModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_weblink", language)}
              </Button>
            }
          />
        }
      />

      {/* Create weblink modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_weblink", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateWeblink}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_weblink", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createWeblinkForm}
                stateName="name"
                onChange={setCreateWeblinkForm}
                defaultLanguage={language}
                languageChoices={
                  weblinksData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(weblinksData[0].name)
                }
              />

              {/* Description */}
              <TextInputMultiLanguage
                id="description"
                type="text"
                label={translateText("description", language)}
                placeholder={translateText(
                  "description_weblink_placeholder",
                  language,
                )}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createWeblinkForm}
                stateName="description"
                onChange={setCreateWeblinkForm}
                defaultLanguage={language}
                languageChoices={
                  weblinksData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(weblinksData[0].name)
                }
              />

              {/* Type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                small={translateText("weblink_type_subtext", language)}
                showLabel={true}
                required={true}
                state={createWeblinkForm}
                stateName="type"
                onChange={setCreateWeblinkForm}
                defaultValue="W"
              >
                {weblinkTypeChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* URL */}
              <TextInput
                id="url"
                type="text"
                label={translateText("url", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createWeblinkForm}
                stateName="url"
                onChange={setCreateWeblinkForm}
              />

              {/* Image */}
              <div>
                <label
                  htmlFor="weblink_image"
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("image", language)}
                </label>
                <UploadFileDropzone
                  id="weblink_image"
                  className=""
                  limit={1}
                  page="weblinks"
                  savedImages={[]}
                  blockId={null}
                />
              </div>

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="target"
                        type="radio"
                        defaultChecked={createWeblinkForm.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateWeblinkForm({
                            ...createWeblinkForm,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {createWeblinkForm.target === "S" && (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllCreateRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForCreateWeblink}
                        checked={createWeblinkForm.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit weblink modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_weblink", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditWeblink}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_weblink", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedWeblink}
                stateName="name"
                onChange={setSelectedWeblink}
                defaultLanguage={language}
                languageChoices={
                  weblinksData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(weblinksData[0].name)
                }
              />

              {/* URL */}
              <div>
                <TextInput
                  id="url"
                  type="text"
                  label={translateText("url", language)}
                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                  required={true}
                  showLabel={true}
                  state={selectedWeblink}
                  stateName="url"
                  onChange={setSelectedWeblink}
                />
                <div className="flex w-full text-left mt-1 group">
                  <a
                    href={selectedWeblink.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-secondary text-left w-full m-auto group-hover:text-primary group-hover:underline"
                  >
                    <ArrowTopRightOnSquareIcon
                      className="h-4 w-4 text-secondary group-hover:text-primary inline-block mr-1"
                      aria-hidden="true"
                    />
                    {translateText("open_url", language)}
                  </a>
                </div>
                {(selectedWeblink.url !== "" || selectedWeblink.url !== null) &&
                  selectedWeblink.qr_code !== null && (
                    <EyeIcon
                      className="mt-3 h-5 w-5 text-black hover:text-info"
                      onMouseEnter={() => setShowQrCode(true)}
                      onMouseLeave={() => setShowQrCode(false)}
                    />
                  )}
                {showQrCode && selectedWeblink.qr_code !== null && (
                  <div>
                    <img
                      className="rounded-md border-0 h-40 mx-auto border-1 border-secondary drop-shadow"
                      src={selectedWeblink.qr_code}
                      alt="web link qr code"
                      aria-hidden="true"
                      height={12}
                    />
                  </div>
                )}
              </div>

              {/* Image */}
              <div>
                <label
                  htmlFor="weblink_edit_image"
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("background_image", language)}
                </label>
                <UploadFileDropzone
                  id="weblink_edit_image"
                  className=""
                  limit={1}
                  page="weblinks"
                  savedImages={getSavedImages()}
                  blockId={null}
                />
              </div>

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="target"
                        type="radio"
                        defaultChecked={selectedWeblink.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setSelectedWeblink({
                            ...selectedWeblink,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {selectedWeblink.target === "S" && (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllEditRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForEditWeblink}
                        checked={selectedWeblink.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText("status_event_subtext", language)}
                showLabel={true}
                toggle={selectedWeblink.status === "A"}
                state={selectedWeblink}
                stateName="status"
                handler={setSelectedWeblink}
                mapping={{ true: "A", false: "I" }}
              />

              {/* Danger zone */}
              <DangerZone
                objectId={selectedWeblink.id}
                handler={confirmDeleteWeblink}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {weblinks.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {weblinksData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {weblinksData.map((weblink: WeblinkState) => (
                    <a
                      href="#!"
                      key={weblink.id}
                      onClick={(e) => selectWeblinkToEdit(e, [weblink.id])}
                      className="relative"
                    >
                      {/* Weblink data */}
                      <Card
                        key={weblink.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {weblink.name[language]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          {getFeatureIcon(weblink)}
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {weblink.datetime_created}
                          </div>
                        </div>
                      </Card>

                      {/* Status indicator */}
                      <Indicator
                        size={3}
                        colour={
                          weblink.status[0] === "A" ? "success" : "warning"
                        }
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_weblinks", language)}
                subtext={translateText("no_weblinks_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
